<template>
  <div v-if="data" class="position-relative" id="finalResultReport">
    <neo-print-wrapper title="Final Exam Report">
      <div class="row align-items-end">
        <div class="col-md-8">
          <h5 class="font-weight-bold mb-0">{{ student.first_name }} {{ student.last_name }}</h5>
          <p class="mb-2">Class: <span class="font-weight-bold">{{ student.enrollment[0].school_class.name }}</span>  | Section: <span class="font-weight-bold">{{ student.enrollment[0].section.name }}</span>  |  Roll No.: <span class="font-weight-bold">{{ student.enrollment[0].roll_no }}</span></p>
        </div>
        <div class="col-md-4 text-right">
          Session: <span class="font-weight-bold">{{data.session.name}}</span>
        </div>
      </div>
      <a-divider />
      <div class="mt-3">
        <vue-custom-scrollbar class="scroll-area">
          <table class="table table-bordered table-sm">
            <thead class="bg-light">
              <!-- Top row -->
              <tr class="text-center">
                <th rowspan="3">Subjects</th>
                <th :colspan="data.class_section_exams.length * 3">
                  Exams
                </th>
                <th :colspan="data.internals.length * 2">Internals</th>
                <th rowspan="2" colspan="5">Final Result</th>
              </tr>
              <!-- Middle row -->
              <tr class="text-center">
                <th colspan="3" v-for="classExam in data.class_section_exams" :key="classExam.id">
                  {{ classExam.exam.name }} <span v-if="getExamWeightage(classExam.exam.id)">({{getExamWeightage(classExam.exam.id)}})</span>
                </th>
                <th colspan="2" v-for="internal in data.internals" :key="internal.internal_assessment.id">
                  {{internal.internal_assessment.name}} <span v-if="getInternalWeightage(internal.internal_assessment.id)">({{getInternalWeightage(internal.internal_assessment.id)}})</span>
                </th>
              </tr>
              <!-- Bittom row -->
              <tr>
                <template v-for="classExam in data.class_section_exams">
                  <th :key="'score' + classExam.id">Score</th>
                  <th :key="'passmark' + classExam.id">Pass</th>
                  <th :key="'fullmark' + classExam.id">Full</th>
                </template>
                <template v-for="internal in data.internals">
                  <th :key="'score' + internal.id">Score</th>
                  <th :key="'fullmark' + internal.id">Full</th>
                </template>
                <th>Score</th>
                <th>Pass</th>
                <th>Full</th>
                <th>Grade</th>
                <th>Credit</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="subject in data.subjects" :key="subject.id">
                <th>{{ subject.abbreviation }}</th>
                <template v-for="classExam in data.class_section_exams">
                  <td :key="subject.id + 'score' + classExam.id"
                  :class="[`${getSubjectExamMark(subject.id, classExam, 'isfailed') ? 'text-danger' : ''}`]"
                  >
                    {{ getSubjectExamMark(subject.id, classExam, 'score') }}
                  </td>
                  <td :key="subject.id + 'passmark' + classExam.id">
                    {{getSubjectExamMark(subject.id, classExam, 'passmark')}}
                  </td>
                  <td :key="subject.id + 'fullmark' + classExam.id">
                    {{getSubjectExamMark(subject.id, classExam, 'fullmark')}}
                  </td>
                </template>
                <template v-for="internal in data.internals">
                  <td :key="subject.id + 'score' + internal.internal_assessment.id">
                    {{ getSubjectInternalMark(subject.id, internal.internal_assessment.id, 'score') }}
                  </td>
                  <td :key="subject.id + 'fullmark' + internal.internal_assessment.id">
                    {{ getSubjectInternalMark(subject.id, internal.internal_assessment.id, 'fullmark') }}
                  </td>
                </template>
                <th :class="[`${getStudentFinalMark(subject.id, 'isfailed') ? 'text-danger' : ''}`]">
                  {{ getStudentFinalMark(subject.id, 'score') }}
                </th>
                <td>{{ getStudentFinalMark(subject.id, 'passmark') }}</td>
                <td>{{ getStudentFinalMark(subject.id, 'fullmark') }}</td>
                <th>{{ getStudentFinalMark(subject.id, 'grade') }}</th>
                <th>{{ getStudentFinalMark(subject.id, 'credit') }}</th>
              </tr>
            </tbody>
          </table>
          <table class="table table-bordered table-sm">
            <thead class="bg-light">
              <tr>
                <th>Total Score</th>
                <th>Full Mark</th>
                <th>Total PC</th>
                <th>Grade</th>
                <th>Credit</th>
                <th>Attendance</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>
                  {{data.student_final_declared_marks.total_mark_score}}
                </th>
                <th>
                  {{data.student_final_declared_marks.total_exam_full_mark}}
                </th>
                <th>
                  {{data.student_final_declared_marks.mark_percentage}}%
                </th>
                <th>
                  {{data.student_final_declared_marks.grade}}
                </th>
                <th>
                  {{data.student_final_declared_marks.credit_point}}
                </th>
                <th>
                  {{data.attendance.present_days}} / {{data.attendance.working_days}} ({{data.attendance.percent}}%)
                </th>
              </tr>
            </tbody>
          </table>
        </vue-custom-scrollbar>
      </div>

      <!-- Print Button -->
      <div class="position-absolute neo-tr-5 d-print-none">
        <button
          type="button"
          class="btn btn-light btn-sm btn-with-addon"
          @click="print"
        >
          <span class="btn-addon">
            <i class="btn-addon-icon fe fe-printer" />
          </span>
          Print
        </button>
      </div>
    </neo-print-wrapper>
  </div>
</template>

<script>
import { find } from 'lodash'
import vueCustomScrollbar from 'vue-custom-scrollbar'
import NeoPrintWrapper from '@/components/layout/Print/Default'
export default {
  props: ['data', 'student'],
  components: {
    vueCustomScrollbar,
    NeoPrintWrapper,
  },
  methods: {
    getSubjectExamMark(subjectId, classExam, type = 'score') {
      const subject = find(classExam.student_exam_marks, { subject_id: subjectId })
      if (subject) {
        if (type === 'score') {
          return subject.exam_mark_score
        }
        if (type === 'passmark') {
          return subject.exam_pass_mark
        }
        if (type === 'fullmark') {
          return subject.exam_full_mark
        }
        if (type === 'isfailed') {
          return subject.is_failed === 1
        }
        if (type === 'isabsent') {
          return subject.is_absent === 1
        }
      }
    },
    getSubjectInternalMark(subjectId, internalId, type = 'score') {
      const internal = find(this.data.internal_assessment_mark, {
        subject_id: subjectId,
        internal_assessment_id: internalId,
      })
      if (internal) {
        if (type === 'score') {
          return internal.final_student_internal_assessment_mark
        }
        if (type === 'fullmark') {
          return internal.full_mark
        }
      }
    },
    getStudentFinalMark(subjectId, type = 'score') {
      const subject = find(this.data.student_final_marks, { subject_id: subjectId })
      if (subject) {
        if (type === 'score') {
          return subject.exam_mark_score
        }
        if (type === 'passmark') {
          return subject.exam_pass_mark
        }
        if (type === 'fullmark') {
          return subject.exam_full_mark
        }
        if (type === 'grade') {
          return subject.exam_mark_grade
        }
        if (type === 'credit') {
          return subject.exam_mark_credit_point
        }
        if (type === 'isfailed') {
          return subject.is_failed === 1
        }
        if (type === 'isabsent') {
          return subject.is_absent === 1
        }
      }
    },
    getExamWeightage (examId) {
      const exam = find(this.data.final_exams, { class_section_exam_id: examId })
      if (exam) {
        return exam.final_exam_weightage
      }
    },
    getInternalWeightage(internalId) {
      const internal = find(this.data.internals, { internal_a_id: internalId })
      if (internal) {
        return internal.internal_assessment_weightage
      }
    },
    print() {
      this.$htmlToPaper('finalResultReport')
    },
  },
}
</script>
